import { BrowserRouter } from 'react-router-dom';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { CssBaseline, GlobalStyles } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import { Portal } from './Portal';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ConfirmationBox from './components/ConfirmationBox';
import AlertBar from './components/AlertBar';
import { useEffect, useState } from 'react';
import { DataService } from './services/data-service';
import { TabProvider } from './contexts/TabContext';
import { useDispatch } from 'react-redux';
import { LoginUser } from './redux/user/userReducer';
import { getUserToken } from './helpers/user-token';
import { IUserStore } from './redux/user/types';
import Loader from './components/Loader';

interface IUserDetailsResponse {
  success: boolean;
  message: string;
  data: {
    Email: string;
    active: boolean;
    Role?: string;
    PhoneNumber: string;
    Id: string;
    Name: string;
  };
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 1, // Retry failed requests once
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes
    },
  },
});

function App() {
  const [booted, setBooted] = useState<boolean>(false);
  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    DataService.setBaseUrl("https://meckin-wms-ckhphphtbrerdmgg.westeurope-01.azurewebsites.net/");
    // DataService.setBaseUrl("http://localhost:5024/");
    
    // Load user details if token exists
    const token = getUserToken();
    if (token) {
      // Check localStorage cache first
      const cachedUserDetails = localStorage.getItem('user-details');
      const cacheTimestamp = localStorage.getItem('user-details-timestamp');
      const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
      
      const isCacheValid = cachedUserDetails && 
                          cacheTimestamp && 
                          (Date.now() - parseInt(cacheTimestamp)) < CACHE_DURATION;

      if (isCacheValid) {
        try {
          // Use cached data
          const cachedUser: IUserStore = JSON.parse(cachedUserDetails);
          dispatch(LoginUser(cachedUser));
          setUserLoaded(true);
        } catch (error) {
          console.warn('Failed to parse cached user details, fetching fresh data:', error);
          // If cache is corrupted, fall back to API call
          fetchUserDetails(token);
        }
      } else {
        // Cache expired or doesn't exist, fetch fresh data
        fetchUserDetails(token);
      }
    } else {
      setUserLoaded(true);
    }

    // Boot the app after a short delay
    setTimeout(() => {
      setBooted(true);
    }, 150);
  }, [dispatch]);

  const fetchUserDetails = async (token: string) => {
    try {
      // Try employee endpoint first
      let response = await DataService.get('api/Employee/details');

      // If employee endpoint fails, try client endpoint
      if (!response.ok) {
        response = await DataService.get('api/client/details');
      }

      if (response.ok) {
        const result: IUserDetailsResponse = await response.json();

        if (result.success) {
          const newStore: IUserStore = {
            token: token,
            isAuthenticated: true,
            user: {
              email: result.data.Email,
              id: result.data.Id,
              name: result.data.Name,
              role: result.data.Role || '',
              phoneNumber: result.data.PhoneNumber,
            }
          };

          // Store in Redux
          dispatch(LoginUser(newStore));

          // Cache in localStorage with timestamp
          localStorage.setItem('user-details', JSON.stringify(newStore));
          localStorage.setItem('user-details-timestamp', Date.now().toString());
        }
      }
    } catch (err) {
      console.warn('Failed to load user details:', err);
      // Clear potentially corrupted cache
      localStorage.removeItem('user-details');
      localStorage.removeItem('user-details-timestamp');
    } finally {
      setUserLoaded(true);
    }
  };

  if (!booted || !userLoaded) {
    return <Loader />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <GlobalStyles styles={{ html: { fontSize: '95%' } }} />
          <BrowserRouter>
            <TabProvider>
              <AlertBar />
              <Portal />
              <ConfirmationBox />
            </TabProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
